export const calculateMarketValue = (quantity, price) => {
  if (!price) return 0.0;
  return quantity * parseFloat(price);
};

export const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';

    // If it's a string, strip all commas before parsing
    const cleanedValue = typeof value === 'string' ? value.replace(/,/g, '') : value;

    const number = parseFloat(cleanedValue); // Ensure it's a number
    if (isNaN(number)) return '-'; // Handle invalid input

    return number.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD', // You can change this to your desired currency
        minimumFractionDigits: 2, // Ensure two decimal places
        maximumFractionDigits: 2, // Ensure two decimal places
    });
};

export const formatDecimalPlaces = (value, decimalPlaces = 2) => {
  // Check for null or undefined input
  if (value === null || value === undefined) {
    return '-';
  }

  // Attempt to convert the input value to a floating-point number
  const number = parseFloat(value);

  // Check if the conversion resulted in NaN (Not-a-Number)
  if (isNaN(number)) {
    return '-'; // Return '-' if input wasn't a valid number
  }

  // Use the toFixed() method which rounds and formats the number
  // to the specified number of decimal places, returning a string.
  return number.toFixed(decimalPlaces);
};

export const getRowKey = (item) => {
  const position = item.position || '';
  const type = item.type || 'stock';
  const optionType = item.option_type || 'none';
  const strike = item.strike_price || 0;
  const expiry = item.expiration_date || 'na';

  return `${position}-${type}-${optionType}-${strike}-${expiry}`;
};

// Helper function to approximate the Cumulative Normal Distribution Function (CDF)
export const normalCDF = (x) => {
  const t = 1.0 / (1.0 + 0.2316419 * Math.abs(x));
  const d = 0.3989422804014327 * Math.exp(-x * x / 2.0);
  const prob = d * t * (0.319381530 + t * (-0.356563782 + t * (1.781477937 + t * (-1.821255978 + t * 1.330274429))));
  return x >= 0 ? 1.0 - prob : prob;
};

// Rough estimate approximation of Black-Scholes Delta (assuming IV = 100% or 1.0)
export const estimateDelta = (underlyingPrice, strikePrice, expirationDate, optionType) => {
  // 1. Clean strings by removing commas before parsing to floats
  const cleanPrice = (val) => {
    if (val === null || val === undefined) return NaN;
    const cleaned = typeof val === 'string' ? val.replace(/,/g, '') : val;
    return parseFloat(cleaned);
  };

  const uPrice = cleanPrice(underlyingPrice);
  const sPrice = cleanPrice(strikePrice);

  // 2. If either price isn't a valid number, or date is missing, exit early
  if (isNaN(uPrice) || isNaN(sPrice) || !expirationDate) return 0;

  // 3. Normalize both dates to midnight to prevent time-of-day shifting
  const expiryDate = new Date(expirationDate + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0); 

  const diffDays = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));

  // If expired or expiring today, fall back to pure intrinsic status (100 or 0)
  if (diffDays <= 0) {
    if (optionType === 'call') return uPrice > sPrice ? 100 : 0;
    return uPrice < sPrice ? -100 : 0;
  }

  const t = diffDays / 365; // Time in years
  const sigma = 0.2;        // Assumed IV factor of 0.2
  const r = 0.05;           // Assumed risk-free rate of 5%

  // Calculate d1 component of Black-Scholes formula
  const d1 = (Math.log(uPrice / sPrice) + (r + (sigma * sigma) / 2) * t) / (sigma * Math.sqrt(t));

  if (optionType === 'call') {
    return Math.round(normalCDF(d1) * 100);
  } else {
    // Puts have negative Delta
    return Math.round((normalCDF(d1) - 1) * 100);
  }
};

export const estimateDeltaOld = (underlyingPrice, strikePrice, expirationDate, optionType) => {
  if (!underlyingPrice || !strikePrice || !expirationDate) return 0;

  // Calculate days to expiry
  const expiryDate = new Date(expirationDate + 'T00:00:00');
  const diffDays = Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24));

  // If expired or expiring today, fall back to pure intrinsic status (1 or 0)
  if (diffDays <= 0) {
    if (optionType === 'call') return underlyingPrice > strikePrice ? 100 : 0;
    return underlyingPrice < strikePrice ? -100 : 0;
  }

  const t = diffDays / 365; // Time in years
  const sigma = 0.2;        // Assumed IV factor of 0.2
  const r = 0.05;           // Assumed risk-free rate of 5%

  // Calculate d1 component of Black-Scholes formula
  const d1 = (Math.log(underlyingPrice / strikePrice) + (r + (sigma * sigma) / 2) * t) / (sigma * Math.sqrt(t));

  if (optionType === 'call') {
    return Math.round(normalCDF(d1) * 100);
  } else {
    // Puts have negative Delta
    return Math.round((normalCDF(d1) - 1) * 100);
  }
};
